import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { clanky } from '../data/clanky';
import SEO from '../common/SEO';
import NotFound from './NotFound';
import SafeIcon from '../common/SafeIcon';

const ClanekPage = () => {
  const { slug } = useParams();
  const clanek = clanky.find(c => c.slug === slug);

  if (!clanek) return <NotFound />;

  const calculateReadingTime = (obsah) => {
    const wordCount = obsah.reduce((acc, curr) => {
      if (curr.text) return acc + curr.text.split(/\s+/).length;
      if (curr.polozky) return acc + curr.polozky.join(' ').split(/\s+/).length;
      return acc;
    }, 0);
    return Math.max(1, Math.ceil(wordCount / 200));
  };

  const schema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": clanek.nadpis,
    "description": clanek.perex,
    "image": clanek.obrazek,
    "datePublished": clanek.datum,
    "author": { "@type": "Organization", "name": "LightWell" },
    "publisher": {
      "@type": "Organization",
      "name": "LightWell",
      "logo": { "@type": "ImageObject", "url": "https://www.lightwell.cz/media/logo-lightwell.webp" }
    }
  };

  return (
    <>
      <SEO 
        title={clanek.seo.title}
        description={clanek.seo.description}
        path={`/know-how/${slug}`}
        schema={schema}
      />
      <div className="pt-20">
        <div className="bg-gray-50 py-4 border-b border-gray-100">
          <div className="max-w-4xl mx-auto px-4 flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest text-gray-400">
            <Link to="/" className="hover:text-blue-600 transition-colors">Úvod</Link>
            <span>/</span>
            <Link to="/know-how" className="hover:text-blue-600 transition-colors">Know-how</Link>
            <span>/</span>
            <span className="text-gray-900 truncate">{clanek.nadpis}</span>
          </div>
        </div>

        <article className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4">
            {clanek.obrazek && (
              <div className="mb-12 rounded-2xl overflow-hidden shadow-lg bg-white border border-gray-100">
                <img src={clanek.obrazek} alt={clanek.nadpis} className="w-full h-auto" />
              </div>
            )}

            <header className="mb-12">
              <div className="flex items-center gap-4 mb-6">
                <span className="bg-black text-white px-3 py-1 rounded-full text-[9px] font-bold uppercase tracking-widest">Know-how</span>
                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  {calculateReadingTime(clanek.obsah)} MIN ČTENÍ
                </span>
                <span className="text-[10px] font-bold text-gray-300 uppercase">/</span>
                <time className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                  {new Date(clanek.datum).toLocaleDateString('cs-CZ')}
                </time>
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold italic uppercase tracking-tighter text-gray-900 leading-tight mb-8">
                {clanek.nadpis}
              </h1>
              <p className="text-xl text-gray-500 leading-relaxed italic border-l-4 border-blue-600 pl-6">
                {clanek.perex}
              </p>
            </header>

            <div className="prose prose-lg max-w-none prose-gray" style={{ maxWidth: '70ch', margin: '0 auto' }}>
              {clanek.obsah.map((blok, i) => {
                switch (blok.typ) {
                  case 'odstavec':
                    return <p key={i} className="text-gray-700 leading-relaxed mb-6 text-lg">{blok.text}</p>;
                  case 'nadpis':
                    return <h2 key={i} className="text-2xl md:text-3xl font-bold uppercase italic tracking-tight text-gray-900 mt-12 mb-6">{blok.text}</h2>;
                  case 'seznam':
                    return (
                      <ul key={i} className="space-y-4 mb-8 list-none p-0">
                        {blok.polozky.map((polozka, j) => (
                          <li key={j} className="flex gap-4 text-gray-700">
                            <div className="w-1.5 h-1.5 bg-blue-600 rounded-full mt-2.5 shrink-0"></div>
                            <span className="text-lg">{polozka}</span>
                          </li>
                        ))}
                      </ul>
                    );
                  case 'vzorec':
                    return (
                      <div key={i} className="my-12 p-8 bg-gray-900 rounded-3xl text-center shadow-xl relative overflow-hidden text-blue-400">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/10 blur-3xl rounded-full"></div>
                        <code className="text-3xl md:text-5xl font-mono font-bold tracking-widest">{blok.text}</code>
                      </div>
                    );
                  case 'obrazek':
                    return (
                      <figure key={i} className="my-16">
                        <div className="bg-white rounded-3xl p-4 md:p-10 border border-gray-100 shadow-inner">
                          <img src={blok.src} alt={blok.popisek} className="w-full h-auto mx-auto" />
                        </div>
                        {blok.popisek && (
                          <figcaption className="mt-4 text-center text-sm text-gray-500 italic max-w-xl mx-auto leading-relaxed">
                            {blok.popisek}
                          </figcaption>
                        )}
                      </figure>
                    );
                  default:
                    return null;
                }
              })}
            </div>

            <footer className="mt-20 pt-10 border-t border-gray-100 flex flex-col md:flex-row justify-between items-center gap-8">
              <Link to="/know-how" className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-400 hover:text-blue-600 transition-colors">
                <SafeIcon name="ArrowLeft" /> Zpět na seznam článků
              </Link>
              <Link to="/kontakt" className="px-10 py-5 bg-gray-900 text-white rounded-2xl font-bold uppercase tracking-widest hover:bg-blue-600 transition-all shadow-xl">
                Poptat konzultaci
              </Link>
            </footer>
          </div>
        </article>
      </div>
    </>
  );
};

export default ClanekPage;